import React from "react";
import { Modal } from "react-bootstrap";
import styled from "styled-components";
import { sanitizePolicyHtml } from "../../views/SubscriptionProjectV2/lib/sanitizeHtml";

/**
 * Policy modal — generic component for Terms / Privacy / Refund.
 *
 * Replaces 3 nearly-identical modal blocks in legacy Subscription.js
 * (~80 lines each). One component, drive variant by `kind` prop.
 *
 * Security:
 *  Legacy code did `html.replace(/style="[^"]*"/g, "")` which is NOT a
 *  sanitizer — it only stripped one attribute. We use DOMPurify with an
 *  allowlist that drops scripts, iframes, event handlers, etc.
 *
 * Note on styled-components + react-bootstrap Modal:
 *  Modal renders into a portal at <body>, so styled wrappers OUTSIDE the
 *  Modal can't reach the modal-dialog/modal-content. We use `dialogClassName`
 *  + a global StyleSheet via `&&` selectors to scope the override.
 */
function PolicyModal({
  show,
  onClose,
  title,
  htmlSource,
  isLoading = false,
  lang = "th",
}) {
  const safeHtml = sanitizePolicyHtml(htmlSource);

  return (
    <StyledModal
      show={show}
      onHide={onClose}
      size="lg"
      centered
      scrollable
      dialogClassName="policy-modal-dialog"
    >
      <Header>
        <Title>{title}</Title>
        <CloseButton type="button" onClick={onClose} aria-label="Close">
          ✕
        </CloseButton>
      </Header>

      <Body>
        {isLoading ? (
          <LoadingState>
            <Dot />
            <Dot />
            <Dot />
            <LoadingText>
              {lang === "en" ? "Loading…" : "กำลังโหลด…"}
            </LoadingText>
          </LoadingState>
        ) : safeHtml ? (
          <Content
            dangerouslySetInnerHTML={{ __html: safeHtml }}
          />
        ) : (
          <Empty>
            {lang === "en" ? "No content available." : "ไม่มีเนื้อหา"}
          </Empty>
        )}
      </Body>

      <Footer>
        <CloseAction type="button" onClick={onClose}>
          {lang === "en" ? "Close" : "ปิด"}
        </CloseAction>
      </Footer>
    </StyledModal>
  );
}

export default PolicyModal;

// ─── Styled ──────────────────────────────────

/**
 * Override react-bootstrap's Modal internals.
 * `&&` doubles specificity so we beat the bundled Bootstrap CSS.
 */
const StyledModal = styled(Modal)`
  && .modal-dialog {
    max-width: 720px;
  }

  && .modal-content {
    border: none;
    border-radius: 16px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
    overflow: hidden;
    background: #ffffff;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 24px 28px 16px;
  border-bottom: 1px solid #f1f3f5;
`;

const Title = styled.h5`
  /* defensive: override any global h5 coloring */
  && {
    margin: 0;
    font-size: 18px;
    font-weight: 700;
    color: #111827;
    line-height: 1.4;
    flex: 1;
    padding-right: 16px;
  }
`;

const CloseButton = styled.button`
  && {
    background: none;
    border: none;
    color: #9ca3af;
    cursor: pointer;
    font-size: 18px;
    line-height: 1;
    padding: 4px 8px;
    margin: -4px -8px 0 0;
    border-radius: 6px;
    transition: background 0.15s, color 0.15s;
    flex-shrink: 0;

    &:hover {
      background: #f3f4f6;
      color: #374151;
    }
  }
`;

const Body = styled.div`
  padding: 24px 28px;
  max-height: 60vh;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 3px;
  }
`;

/**
 * Style the policy HTML coming from backend. Tags allowed by sanitizeHtml.js
 * are h1-h6, p, ul/ol/li, strong, em, a, blockquote, table.
 *
 * We keep selectors loose since the HTML structure is admin-authored and
 * may vary per policy.
 */
const Content = styled.div`
  font-size: 14px;
  line-height: 1.7;
  color: #374151;

  h1, h2, h3, h4, h5, h6 {
    color: #111827;
    font-weight: 700;
    margin: 24px 0 12px;
    line-height: 1.3;
  }
  h1 { font-size: 22px; }
  h2 { font-size: 19px; }
  h3 { font-size: 16px; }
  h4, h5, h6 { font-size: 14px; }

  /* First heading flush with top */
  & > *:first-child { margin-top: 0; }

  p {
    margin: 0 0 12px;
  }

  ul, ol {
    margin: 0 0 16px;
    padding-left: 24px;

    li {
      margin-bottom: 6px;
    }
  }

  a {
    color: #6366f1;
    text-decoration: underline;

    &:hover {
      color: #4f46e5;
    }
  }

  strong, b {
    font-weight: 600;
    color: #111827;
  }

  blockquote {
    border-left: 3px solid #e5e7eb;
    padding: 8px 16px;
    margin: 16px 0;
    color: #6b7280;
    font-style: italic;
  }

  table {
    border-collapse: collapse;
    width: 100%;
    margin: 16px 0;
    font-size: 13px;

    th, td {
      border: 1px solid #e5e7eb;
      padding: 8px 12px;
      text-align: left;
    }
    th {
      background: #f9fafb;
      font-weight: 600;
      color: #111827;
    }
  }

  hr {
    border: none;
    border-top: 1px solid #e5e7eb;
    margin: 24px 0;
  }
`;

const Empty = styled.div`
  text-align: center;
  padding: 48px 16px;
  color: #9ca3af;
  font-size: 14px;
`;

// ─── Loading dots ──────────────────────────────

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 64px 16px;
`;

const Dot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #6366f1;
  animation: bounce 1.4s ease-in-out infinite both;

  &:nth-child(1) { animation-delay: -0.32s; }
  &:nth-child(2) { animation-delay: -0.16s; }

  @keyframes bounce {
    0%, 80%, 100% {
      transform: scale(0.6);
      opacity: 0.5;
    }
    40% {
      transform: scale(1);
      opacity: 1;
    }
  }
`;

const LoadingText = styled.span`
  margin-left: 8px;
  font-size: 13px;
  color: #6b7280;
`;

// ─── Footer ──────────────────────────────

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 16px 28px;
  border-top: 1px solid #f1f3f5;
  background: #fafbfc;
`;

const CloseAction = styled.button`
  && {
    height: 40px;
    padding: 0 24px;
    background: #ffffff;
    color: #374151;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s;
    font-family: inherit;

    &:hover {
      border-color: #9ca3af;
      color: #111827;
      background: #f9fafb;
    }
  }
`;